import React, { useState, useEffect } from 'react';
import { Settings, Save, Loader2, AlertCircle, Check, RotateCcw, Sliders } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminMatchingAlgorithm = () => {
  const [settings, setSettings] = useState({
    age_weight: 0.2,
    location_weight: 0.3,
    education_weight: 0.15,
    profession_weight: 0.15,
    religion_weight: 0.2,
    min_match_score: 0.5,
    show_matches_count: 20,
    prioritize_premium: true,
    prioritize_verified: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const fetchSettings = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API_URL}/api/admin/phase2/matching/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load matching settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    const token = localStorage.getItem('token');
    setSaving(true);
    setError(null);

    // Validate weights sum to 1
    const totalWeight = settings.age_weight + settings.location_weight + 
                       settings.education_weight + settings.profession_weight + 
                       settings.religion_weight;
    
    if (Math.abs(totalWeight - 1) > 0.01) {
      setError(`Weights must sum to 1.0 (currently ${totalWeight.toFixed(2)})`);
      setSaving(false);
      return;
    }

    try {
      await axios.put(`${API_URL}/api/admin/phase2/matching/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving settings:', err);
      setError(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    setSettings({
      age_weight: 0.2,
      location_weight: 0.3,
      education_weight: 0.15,
      profession_weight: 0.15,
      religion_weight: 0.2,
      min_match_score: 0.5,
      show_matches_count: 20,
      prioritize_premium: true,
      prioritize_verified: true
    });
  };

  const handleWeightChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: parseFloat(value)
    }));
  };

  const totalWeight = settings.age_weight + settings.location_weight + 
                     settings.education_weight + settings.profession_weight + 
                     settings.religion_weight;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-mint-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-matching-algorithm">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Matching Algorithm</h1>
          <p className="text-gray-600 mt-1">Configure how matches are calculated and displayed</p>
        </div>
        <Sliders className="w-10 h-10 text-mint-500" />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-500" />
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Check className="w-5 h-5 text-green-500" />
          <p className="text-green-700">{success}</p>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Weight Settings */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-mint-500" />
            Matching Weights
          </h3>
          <p className="text-sm text-gray-500 mb-4">
            Adjust how much each factor influences match scores. Total must equal 1.0
          </p>

          <div className="space-y-4">
            {[
              { key: 'age_weight', label: 'Age Compatibility', desc: 'Weight for age preference matching' },
              { key: 'location_weight', label: 'Location', desc: 'Weight for geographic proximity' },
              { key: 'education_weight', label: 'Education', desc: 'Weight for education level matching' },
              { key: 'profession_weight', label: 'Profession', desc: 'Weight for career compatibility' },
              { key: 'religion_weight', label: 'Religion', desc: 'Weight for religious preference matching' }
            ].map((item) => (
              <div key={item.key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">{item.label}</label>
                  <span className="text-sm text-gray-500">{(settings[item.key] * 100).toFixed(0)}%</span>
                </div>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={settings[item.key]}
                  onChange={(e) => handleWeightChange(item.key, e.target.value)}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-mint-500"
                  data-testid={`slider-${item.key}`}
                />
                <p className="text-xs text-gray-400">{item.desc}</p>
              </div>
            ))}

            <div className={`p-3 rounded-lg ${Math.abs(totalWeight - 1) > 0.01 ? 'bg-red-50' : 'bg-green-50'}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Total Weight:</span>
                <span className={`font-bold ${Math.abs(totalWeight - 1) > 0.01 ? 'text-red-600' : 'text-green-600'}`}>
                  {(totalWeight * 100).toFixed(0)}%
                </span>
              </div>
              {Math.abs(totalWeight - 1) > 0.01 && (
                <p className="text-xs text-red-600 mt-1">Weights must sum to 100%</p>
              )}
            </div>
          </div>
        </div>

        {/* Display Settings */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Display Settings</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Match Score (%)
                </label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={(settings.min_match_score * 100).toFixed(0)}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    min_match_score: parseInt(e.target.value) / 100
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                  data-testid="input-min-score"
                />
                <p className="text-xs text-gray-500 mt-1">Only show matches above this score</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Matches to Show
                </label>
                <input
                  type="number"
                  min="5"
                  max="100"
                  value={settings.show_matches_count}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    show_matches_count: parseInt(e.target.value)
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mint-500 focus:border-transparent"
                  data-testid="input-match-count"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum number of matches displayed per page</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Priority Settings</h3>

            <div className="space-y-4">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-gray-700">Prioritize Premium Users</span>
                  <p className="text-xs text-gray-500">Show premium members higher in results</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.prioritize_premium}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    prioritize_premium: e.target.checked
                  }))}
                  className="w-5 h-5 rounded border-gray-300 text-mint-500 focus:ring-mint-500"
                  data-testid="toggle-premium"
                />
              </label>

              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-gray-700">Prioritize Verified Users</span>
                  <p className="text-xs text-gray-500">Show verified members higher in results</p>
                </div>
                <input
                  type="checkbox"
                  checked={settings.prioritize_verified}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    prioritize_verified: e.target.checked
                  }))}
                  className="w-5 h-5 rounded border-gray-300 text-mint-500 focus:ring-mint-500"
                  data-testid="toggle-verified"
                />
              </label>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={resetToDefaults}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RotateCcw className="w-4 h-4" />
          Reset to Defaults
        </button>
        <button
          onClick={handleSave}
          disabled={saving || Math.abs(totalWeight - 1) > 0.01}
          className="flex items-center gap-2 px-6 py-2 bg-mint-500 text-white rounded-lg hover:bg-mint-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Settings
        </button>
      </div>
    </div>
  );
};

export default AdminMatchingAlgorithm;
